import { useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslations } from "use-intl";
import { useAppLocale } from "../i18n";
import { caseflowOrigin, caseflowSupportConfig, isPublicSupportLocation } from "../lib/caseflow-support";

type WidgetApi = { open?: () => void; unmount?: () => void; setDiagnosticsEnabled?: (enabled: boolean) => void };
const widgetApi = () => (window as Window & { CaseflowWidget?: WidgetApi }).CaseflowWidget;
const scriptId = "flowtrace-caseflow-widget-script";
const hostId = "caseflow-widget-host";
const config = caseflowSupportConfig(import.meta.env);

export const CaseflowContactButton = () => {
  const t = useTranslations("support");
  const { locale } = useAppLocale();
  const { pathname, search, hash } = useLocation();
  const [status, setStatus] = useState<"idle" | "loading" | "open" | "error">("idle");
  const epoch = useRef(0);
  const allowed = isPublicSupportLocation(pathname, search, hash);

  useLayoutEffect(() => {
    setStatus("idle");
    return () => {
      epoch.current++;
      widgetApi()?.unmount?.();
      document.getElementById(scriptId)?.remove();
      document.getElementById(hostId)?.remove();
    };
  }, [pathname, search, hash, locale]);

  if (!config || !allowed) return null;

  const openContact = () => {
    if (status === "loading") return;
    if (status === "open") { widgetApi()?.open?.(); return; }
    const attempt = ++epoch.current;
    setStatus("loading");
    try {
      // CaseFlow's config API is same-origin only. Validate its site/project
      // mapping with check:caseflow-support before enabling the build; the
      // existing iframe owns config reads and intake authorization at runtime.
      const script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.src = `${caseflowOrigin}/widget.js`;
      Object.assign(script.dataset, {
        site: config.siteKey, projectId: config.projectId,
        baseUrl: caseflowOrigin, locale: locale === "ja-JP" ? "ja" : "en",
        label: t("contact"), view: "intake", theme: "dark", accent: "#c8f06a",
        llm: String(config.llm), autoOpen: "false", debug: "false",
      });
      script.onload = () => {
        if (attempt !== epoch.current) {
          widgetApi()?.unmount?.();
          document.getElementById(hostId)?.remove();
          script.remove();
          return;
        }
        // Widget conversation inputs are never part of FlowTrace recordings.
        document.getElementById(hostId)?.setAttribute("data-flowtrace-block", "");
        widgetApi()?.setDiagnosticsEnabled?.(false);
        if (!widgetApi()?.open) { script.remove(); setStatus("error"); return; }
        widgetApi()?.open?.();
        setStatus("open");
      };
      script.onerror = () => { script.remove(); if (attempt === epoch.current) setStatus("error"); };
      document.body.appendChild(script);
    } catch {
      if (attempt === epoch.current) setStatus("error");
    }
  };

  return (
    <span className="site-footer-contact" data-flowtrace-block="">
      <button type="button" onClick={openContact} disabled={status === "loading"}>
        {status === "loading" ? t("loading") : t("contact")}
      </button>
      {status === "error" && <small role="alert">{t("unavailable")}</small>}
    </span>
  );
};
